import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import './portal-v2.css';

const PortalV2ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => { setToken(params.get('token') || ''); }, [params]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) return setError('Passwords do not match');
    setLoading(true);
    const result = await resetPassword(token, password);
    setLoading(false);
    if (result.success) {
      setSuccess(true);
      setTimeout(() => navigate('/portal/login'), 1800);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="portal-v2">
      <div className="pv2-login-shell">
        <div className="pv2-login-card" data-testid="pv2-reset-card">
          <div className="pv2-mono-tag" style={{ marginBottom: 14 }}>Password Reset · WesTex Legal</div>
          <h1 className="pv2-display" style={{ fontSize: 36, marginBottom: 8 }}>
            Choose a new <span className="pv2-display-italic">password.</span>
          </h1>

          {!token && (
            <div className="pv2-alert pv2-alert-red" style={{ marginTop: 20 }}>
              <AlertCircle size={14} /><span>This reset link is invalid or expired. Request a new one from the sign-in page.</span>
            </div>
          )}

          {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}
          {success && <div className="pv2-alert pv2-alert-green"><CheckCircle size={14} /><span>Password updated. Redirecting…</span></div>}

          {token && !success && (
            <form onSubmit={handleSubmit} data-testid="pv2-reset-form" style={{ marginTop: 20 }}>
              <div className="pv2-field">
                <label className="pv2-label">New password</label>
                <div style={{ position: 'relative' }}>
                  <input className="pv2-input" type={showPwd ? 'text' : 'password'} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} style={{ paddingRight: 44 }} data-testid="pv2-reset-pwd" />
                  <button type="button" onClick={() => setShowPwd(s => !s)} aria-label="Toggle password" style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 44, background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}>
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="pv2-field">
                <label className="pv2-label">Confirm new password</label>
                <input className="pv2-input" type={showPwd ? 'text' : 'password'} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pv2-reset-confirm" />
              </div>
              <button type="submit" disabled={loading} className="pv2-btn pv2-btn-primary" style={{ width: '100%' }} data-testid="pv2-reset-submit">
                {loading ? 'Updating…' : 'Update Password'}
              </button>
            </form>
          )}

          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--pv2-ink-line)' }}>
            <Link to="/portal/login" className="pv2-mono-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--pv2-text-subtle)' }}>
              <ArrowLeft size={12} /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalV2ResetPassword;
